import React from 'react';
import { Group, Rect, Text } from 'react-konva';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';
import { z } from 'zod';

/**
 * `compass_env_label` — a synthetic non-interactive text shape used
 * by the Compass Explorer to mark environment groups (prod / stage /
 * dev / other) at L1/L2. Kept behind its own manifest so the label's
 * rendering and sizing stay decoupled from compass_bubble.
 */

const envLabelProps = z.object({
  text: z.string().default(''),
  accent: z.string().default('#475569'),
  count: z.number().default(0),
});

export type CompassEnvLabelProps = z.infer<typeof envLabelProps>;

const CompassEnvLabel: React.FC<{
  viewModel: { width: number; height: number };
  props: Record<string, unknown>;
}> = ({ viewModel, props }) => {
  const { width: w, height: h } = viewModel;
  const text = String(props.text ?? '');
  const accent = String(props.accent ?? '#475569');
  const count = Number(props.count ?? 0);
  const labelW = Math.max(64, Math.min(136, 22 + text.length * 8));
  return (
    <Group listening={false}>
      <Rect
        x={0}
        y={h - 25}
        width={labelW}
        height={18}
        fill={`${accent}1a`}
        cornerRadius={9}
        listening={false}
      />
      <Text
        x={10}
        y={h - 21}
        width={labelW - 20}
        text={text.toUpperCase()}
        fontSize={10}
        fontStyle="bold"
        fontFamily="DM Sans, Inter, Arial, sans-serif"
        fill={accent}
        letterSpacing={1}
        listening={false}
      />
      <Text
        x={labelW + 10}
        y={h - 21}
        width={Math.max(40, w - labelW - 10)}
        text={`· ${count}`}
        fontSize={12}
        fontStyle="bold"
        fontFamily="DM Sans, Inter, Arial, sans-serif"
        fill="#66758f"
        letterSpacing={0.5}
        listening={false}
      />
    </Group>
  );
};

export const CompassEnvLabelManifest: ShapeManifest<CompassEnvLabelProps> = {
  id: 'compass_env_label',
  version: '1.0.0',
  category: 'network',
  icon: 'text',
  propsSchema: envLabelProps,
  defaultProps: () => ({ text: '', accent: '#475569', count: 0 }),
  defaultDimensions: { width: 200, height: 24 },
  anchors: { snap: [], perimeter: 'rect' },
  capabilities: {
    selectable: false,
    transformable: false,
    textEditable: false,
    connectable: false,
  },
  properties: [],
  acl: () => true,
  renderers: {
    konvaNative: (args) =>
      React.createElement(CompassEnvLabel, {
        viewModel: args.viewModel as { width: number; height: number },
        props: args.props as Record<string, unknown>,
      }),
  },
};
